import { ADMIN_USER_TYPES, type StaffListItem } from '@/definitions/staff'

import { staffDisplayName } from './staffLabels'

export interface StaffRow {
  staff: StaffListItem
  isAdmin: boolean
}

/**
 * Alphabetical by the name the card shows, so the list reads in the order it
 * looks like it should. Case and accents are ignored; email breaks a tie, so
 * two staff members sharing a name keep a fixed order between renders.
 */
const byDisplayName = (first: StaffListItem, second: StaffListItem): number => {
  const byName = staffDisplayName(first).localeCompare(
    staffDisplayName(second),
    undefined,
    { sensitivity: 'base' }
  )
  return byName !== 0 ? byName : first.email.localeCompare(second.email)
}

/**
 * Orders an organization's staff for the tab: administrators first, then
 * alphabetically within each group.
 *
 * The admin is who CS is looking for — they are the person a practice's account
 * questions go through — so they lead the list rather than sitting wherever the
 * response happened to place them. The response itself comes back in no
 * meaningful order, which is why everyone else is sorted rather than left as it
 * arrived.
 *
 * Whether a staff member is an administrator is decided here rather than at the
 * point of display, because two `user_type` values mean it and only one of them
 * reads like it does.
 */
export const buildStaffRows = (staff: StaffListItem[]): StaffRow[] => {
  const rows = staff.map((member) => ({
    staff: member,
    isAdmin: ADMIN_USER_TYPES.includes(member.user_type),
  }))

  return rows.sort((first, second) => {
    if (first.isAdmin !== second.isAdmin) {
      return Number(second.isAdmin) - Number(first.isAdmin)
    }
    return byDisplayName(first.staff, second.staff)
  })
}

/**
 * Whether this staff member is the only administrator the organization has left
 * with access.
 *
 * Asked of the whole roster rather than the filtered list, so the answer does not
 * change with what the operator happens to be looking at.
 */
export const isOnlyActiveAdmin = (rows: StaffRow[], staffId: string): boolean => {
  const activeAdminIds = rows
    .filter((row) => row.isAdmin && row.staff.enabled)
    .map((row) => row.staff.id)

  return activeAdminIds.length === 1 && activeAdminIds.includes(staffId)
}
